/**
 * Everything the New Campaign screen holds, and the one list it adds up to.
 *
 * The model lives here rather than beside the page because **both sides need
 * it**. The browser reads it out of the DOM to count segments against the
 * longest promo code in the list; the server reads it out of a posted form to
 * validate and save. If each had its own idea of what "the current recipient
 * list" is, the count on screen and the campaign in the database would
 * eventually disagree — and the way that failure looks is a bill for twice
 * what was quoted.
 *
 * One reader, `readCampaignInput`, used for a query string and a posted form
 * alike, so the re-render after a refusal (T3.7, T3.8) puts every field back
 * exactly as it was submitted rather than as a near-miss of it.
 *
 * Pure by construction (PLAN §4): `URLSearchParams` is a language global, not
 * a door to the outside.
 */

import {
  DEFAULT_SEPARATOR,
  DUPLICATE_RESOLUTIONS,
  DEFAULT_DUPLICATE_RESOLUTION,
  IMPORT_SEPARATOR,
  SEPARATORS,
  parsePairedLines,
  type DuplicateResolution,
  type ParsedPair,
  type SeparatorName,
} from './recipients.ts';
import { parseTwoBlocks } from './two-blocks.ts';

export const INPUT_MODES = ['pairs', 'blocks', 'file'] as const;

export type InputMode = (typeof INPUT_MODES)[number];

/**
 * Two blocks — the mode the screen opens in.
 *
 * Paired lines were the default the specification named (§7.1), and they are
 * the better format on paper: each line carries its own pairing, so a stray
 * line costs one recipient rather than every recipient below it. In practice
 * the lists arrive as two columns out of a spreadsheet, and asking for them
 * joined meant either a formula somebody wrote by hand or a paste into the
 * wrong mode. Two boxes take the columns as they come.
 *
 * It is a default, not a decision: all three modes are one press away, and the
 * one that is selected is the only one read (`recipientPairs`).
 */
export const DEFAULT_INPUT_MODE: InputMode = 'blocks';

/**
 * Anything unrecognised falls back to the default rather than being refused.
 * The value arrives in a query string, where the only realistic sources are a
 * stale bookmark and a typed URL; neither is worth an error page, and the
 * fallback is the mode the screen would have opened in anyway.
 */
export function isInputMode(value: string | null | undefined): value is InputMode {
  return value !== null && value !== undefined && (INPUT_MODES as readonly string[]).includes(value);
}

export function inputMode(value: string | null | undefined): InputMode {
  return isInputMode(value) ? value : DEFAULT_INPUT_MODE;
}

/** In the order they are offered; `recipients.ts` decides what each one is. */
export const SEPARATOR_NAMES = Object.keys(SEPARATORS) as readonly SeparatorName[];

/** Same reasoning as `inputMode`, for the paired-lines separator. */
export function separatorName(value: string | null | undefined): SeparatorName {
  return (SEPARATOR_NAMES as readonly string[]).includes(value ?? '')
    ? (value as SeparatorName)
    : DEFAULT_SEPARATOR;
}

export interface CampaignInput {
  readonly mode: InputMode;
  readonly separator: SeparatorName;
  /** Mode A's textarea, verbatim. */
  readonly pairs: string;
  /**
   * Mode B's two lists, joined by the blank line that separates them.
   *
   * The screen draws two boxes side by side and joins them here, rather than
   * carrying two fields, because **this string is what both sides validate**:
   * the browser reaches the verdict on it while the screen is being filled in,
   * and the gate re-runs the same function on the same string. A second field
   * would be a second thing to keep in step, and the failure would be a screen
   * that says one thing and a database that holds another.
   *
   * Verbatim, blank lines and all — they are the syntax, and a blank line
   * inside one of the boxes is a third block and gets said out loud.
   */
  readonly blocks: string;
  /**
   * Mode C's list, one `phone<TAB>code` per line.
   *
   * The file itself never leaves the browser (PLAN §9), so this field is the
   * import: what the reader made of the spreadsheet, in the form the server
   * parses every other paste in. It is also the only part of an import a
   * re-render can restore — a file input cannot be given its file back, and
   * nothing should want to re-read a file it has already read.
   */
  readonly imported: string;
  /** The file it came from, so the panel can name what is loaded. Display only. */
  readonly importName: string;
  /** The message, with `%promo%` wherever the code belongs (PLAN §6). */
  readonly template: string;
  /**
   * Which row of a repeated number gets sent (specification §16).
   *
   * Empty means undecided, which is what a screen nobody has answered the
   * question on posts — and what the browser blocks Continue on. The server
   * still has to send *something*, so `duplicateResolution` falls back to
   * `first`; the block is what makes sure it is never reached by accident.
   */
  readonly duplicates: DuplicateResolution | '';
  /** For `manual`: the lines to keep, one per repeated number. */
  readonly keep: readonly number[];
}

export const EMPTY_INPUT: CampaignInput = {
  mode: DEFAULT_INPUT_MODE,
  separator: DEFAULT_SEPARATOR,
  pairs: '',
  blocks: '',
  imported: '',
  importName: '',
  template: '',
  duplicates: '',
  keep: [],
};

export function readCampaignInput(source: URLSearchParams): CampaignInput {
  return {
    mode: inputMode(source.get('mode')),
    separator: separatorName(source.get('separator')),
    // Not trimmed: a leading blank line is a line the administrator can count
    // to, and every problem this screen reports is reported by line number.
    pairs: source.get('pairs') ?? '',
    blocks: source.get('blocks') ?? '',
    imported: source.get('imported') ?? '',
    importName: source.get('importName') ?? '',
    template: source.get('template') ?? '',
    duplicates: duplicateChoice(source.get('duplicates')),
    keep: keptLines(source.get('keep')),
  };
}

/** Undecided stays undecided here; only `resolveDuplicates` supplies the fallback. */
export function duplicateChoice(value: string | null | undefined): DuplicateResolution | '' {
  return (DUPLICATE_RESOLUTIONS as readonly string[]).includes(value ?? '')
    ? (value as DuplicateResolution)
    : '';
}

/** What the screen actually validates by, once the question is settled or not. */
export function resolveDuplicates(input: CampaignInput): DuplicateResolution {
  return input.duplicates === '' ? DEFAULT_DUPLICATE_RESOLUTION : input.duplicates;
}

/**
 * The manual choices, as a comma-separated list of line numbers.
 *
 * Anything that is not a positive whole number is dropped rather than refused:
 * the field is written by the browser, and a malformed one should cost the
 * choice it could not read, not the ten thousand rows beside it.
 */
export function keptLines(value: string | null | undefined): readonly number[] {
  if (value === null || value === undefined || value.trim() === '') return [];

  return value
    .split(',')
    .map((part) => Number(part.trim()))
    .filter((line) => Number.isInteger(line) && line > 0);
}

/**
 * The recipient list this screen currently describes.
 *
 * **The selected mode decides, and only the selected mode.** All three panels
 * keep whatever was pasted into them, so text sits in two of them at once
 * routinely; reading anything but the checked tab would silently send a list
 * nobody is looking at. That is the one mistake this screen must not make.
 *
 * Pairs rather than recipients, because the caller decides what to do next:
 * the browser wants the promo codes to size a message, the server wants
 * `validatePairs` and a database row.
 */
export function recipientPairs(input: CampaignInput): readonly ParsedPair[] {
  switch (input.mode) {
    case 'pairs':
      return parsePairedLines(input.pairs, input.separator);
    case 'blocks':
      // The pairs the two blocks agree on. A mismatched paste yields the
      // aligned prefix, which is what the preview shows and what a count of
      // segments should be based on; the mismatch itself is refused elsewhere.
      return parseTwoBlocks(input.blocks).pairs;
    case 'file':
      return parsePairedLines(input.imported, IMPORT_SEPARATOR);
  }
}

/** Every promo code in the current list, including the ones that will not send. */
export function promoCodes(input: CampaignInput): readonly string[] {
  return recipientPairs(input).map((pair) => pair.promoCode);
}
